import { createClient } from "@/lib/generated/client";
import type { Client } from "@/lib/generated/client";
import * as generatedSdk from "@/lib/generated/sdk.gen";
import { getBackendUrl } from "@/lib/environment-url";

type GeneratedSdk = typeof generatedSdk;

let cachedClient: Client | null = null;

function buildCredentialedRequest(input: RequestInfo | URL, init?: RequestInit): Request {
  const source =
    input instanceof Request
      ? input
      : new Request(input instanceof URL ? input.toString() : input, init);
  const headers = new Headers(source.headers);

  if (source.method.toUpperCase() === "GET" && !headers.has("Cache-Control")) {
    headers.set("Cache-Control", "no-cache");
  }

  return new Request(source, {
    credentials: "include",
    headers,
  });
}

const credentialedFetch: typeof fetch = (input, init) => {
  const request = buildCredentialedRequest(input, init);
  return fetch(request);
};

function createBoundClient(): Client {
  const baseUrl = getBackendUrl();
  return createClient({
    baseUrl,
    fetch: credentialedFetch,
    responseStyle: "fields",
    throwOnError: false,
  });
}

function getClient(): Client {
  if (!cachedClient) {
    cachedClient = createBoundClient();
  }
  return cachedClient;
}

export function withClient<T>(consumer: (client: Client) => T): T {
  return consumer(getClient());
}

function withClientOptions<T extends Record<string, unknown> | undefined>(
  options?: T,
): T extends undefined ? { client: Client } : T & { client: Client } {
  const base = options && typeof options === "object" ? { ...options } : {};
  return {
    ...base,
    client: getClient(),
  } as T extends undefined ? { client: Client } : T & { client: Client };
}

const boundSdk = new Proxy(generatedSdk, {
  get(target, prop, receiver) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const value = Reflect.get(target, prop, receiver);
    if (typeof value !== "function") {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return value;
    }

    return (options?: Record<string, unknown>) => {
      // The generated SDK expects a single options argument; ensure we always attach the shared client
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
      return value(withClientOptions(options));
    };
  },
}) as GeneratedSdk;

export const sdk = boundSdk;

export function resetGeneratedClientCacheForTests(): void {
  cachedClient = null;
}
